import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';

import api, { refreshAccessToken } from '../services/api';
import {
  setToken,
  clearToken,
  onLogout,
  getToken,
  setRefreshToken,
  getRefreshToken,
} from '../services/tokenManager';

const AuthContext = createContext({});

const AuthProvider = ({ children }) => {
  const [data, setData] = useState({});
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    const unsubscribe = onLogout(() => {
      setData({});
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    async function bootstrap() {
      try {
        if (getRefreshToken()) {
          await refreshAccessToken();
          const meResponse = await api.get('profile/me');
          const user = meResponse.data;
          setData({ token: getToken(), user });
        } else {
          setData({});
        }
      } catch (e) {
        setData({});
      } finally {
        setIsBootstrapping(false);
      }
    }

    bootstrap();
  }, []);

  const signIn = useCallback(async ({ username, password }) => {
    const response = await api.post('sessions', { username, password });
    const { token, refresh_token: refreshToken, user } = response.data;
    setToken(token);
    if (refreshToken) setRefreshToken(refreshToken);
    setData({ token, user });
  }, []);

  const signOut = useCallback(async () => {
    try {
      const rt = getRefreshToken();
      if (rt) {
        await api.post(
          'sessions/logout',
          { refresh_token: rt },
          {
            headers: { Authorization: `Refresh ${rt}` },
          },
        );
      }
    } catch (e) {
      // ignore
    } finally {
      clearToken();
      setData({});
    }
  }, []);

  const updateUser = useCallback(
    user => {
      setData({
        token: data.token,
        user,
      });
    },
    [data.token],
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        isLoading: isBootstrapping,
        signIn,
        signOut,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { useAuth, AuthProvider };
